import { Award, ChevronRight } from 'lucide-react'
import React, { useMemo } from 'react'
import { DASHBOARD_RANKING_COLUMNS, FIELD_KEYS, getFieldValue } from '../../config/columns'
import { CONFIG } from '../../config/constants'
import { useColumnVisibility } from '../../hooks/useColumnVisibility'
import { getSortedOptions, getStrikeStep } from '../../lib/analysis-utils'
import { getStrikeLabel } from '../../lib/table-utils'
import { cn } from '../../lib/utils'
import { AnalysisMode, type AnalysisResult, type MarketData, type OptionData } from '../../types/index'
import { type ColumnConfig, ColumnSelector } from '../ColumnSelector'
import { GhostCell } from '../ui/ghost-table/DataCells'
import { GhostTh } from '../ui/ghost-table/StickyHeader'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
import { TableWrapper } from '../ui/ghost-table/TableWrapper'
import { WidgetErrorBoundary } from '../ui/WidgetErrorBoundary'
import { useFastMount } from '../../hooks/useFastMount'

interface DashboardRankingTableProps {
    marketData: MarketData | null
    settings: any
    analysis: AnalysisResult | null
    className?: string
}

export const DashboardRankingTable: React.FC<DashboardRankingTableProps> = (props) => (
    <WidgetErrorBoundary fallbackDescription="Ranking Table">
        <DashboardRankingTableContent {...props} />
    </WidgetErrorBoundary>
)

const rowAreEqual = (prev: any, next: any) => {
    return prev.row.ce.ltp === next.row.ce.ltp &&
           prev.row.pe.ltp === next.row.pe.ltp &&
           prev.row.ce.volume === next.row.ce.volume &&
           prev.row.pe.volume === next.row.pe.volume &&
           prev.row.ce.oi === next.row.ce.oi &&
           prev.row.pe.oi === next.row.pe.oi &&
           prev.row.ce.priceChange === next.row.ce.priceChange &&
           prev.row.pe.priceChange === next.row.pe.priceChange &&
           prev.row.strike === next.row.strike &&
           prev.isCeAtm === next.isCeAtm &&
           prev.isPeAtm === next.isPeAtm &&
           prev.isVisible === next.isVisible &&
           prev.analysis?.supportStrike === next.analysis?.supportStrike &&
           prev.analysis?.resistanceStrike === next.analysis?.resistanceStrike;
};

const DashboardRankingRow = React.memo(({ row, idx, analysis, isVisible, maxValues, settingsMode, isCeAtm, isPeAtm }: any) => {
    // CE Values
    const ceVol = getFieldValue(row.ce, FIELD_KEYS.CE_VOL) || 0
    const ceOI = getFieldValue(row.ce, FIELD_KEYS.CE_OI) || 0
    const ceLtp = getFieldValue(row.ce, FIELD_KEYS.CE_LTP) || 0
    const ceChangeP = getFieldValue(row.ce, FIELD_KEYS.CE_PRICE_CHG) || 0
    const ceIV = getFieldValue(row.ce, FIELD_KEYS.CE_IV)
    const ceStrike = getFieldValue(row.ce, FIELD_KEYS.STRIKE)
    const ceLabel = getStrikeLabel(ceStrike, analysis)

    // PE Values
    const peVol = getFieldValue(row.pe, FIELD_KEYS.PE_VOL) || 0
    const peOI = getFieldValue(row.pe, FIELD_KEYS.PE_OI) || 0
    const peLtp = getFieldValue(row.pe, FIELD_KEYS.PE_LTP) || 0
    const peChangeP = getFieldValue(row.pe, FIELD_KEYS.PE_PRICE_CHG) || 0
    const peIV = getFieldValue(row.pe, FIELD_KEYS.PE_IV)
    const peStrike = getFieldValue(row.pe, FIELD_KEYS.STRIKE)
    const peLabel = getStrikeLabel(peStrike, analysis)

    const tdBase =
        'p-2 h-[56px] align-middle whitespace-nowrap border-b border-slate-100/30 dark:border-slate-800/30 bg-white dark:bg-slate-900 group-hover:bg-white dark:group-hover:bg-slate-800 transition-all'
    const callBg = 'bg-[#fffcfc]/30 dark:bg-rose-950/10'
    const putBg = 'bg-[#f6fdf9]/30 dark:bg-emerald-950/10'

    const rowKey = `${ceStrike}-${peStrike}`
    return (
        <tr
            key={rowKey}
            className="group hover:opacity-100 hover:scale-[1.002] hover:shadow-xl hover:z-20 transition-all duration-200 relative"
        >
            {/* CE Side */}
            {isVisible('iv') && (
                <td className={cn(tdBase, callBg, 'text-right first:rounded-l-[12px]')}>
                    <GhostCell
                        val={ceIV || 0}
                        max={maxValues.maxIV}
                        fmt={typeof ceIV === 'number' ? ceIV.toFixed(1) : '-'}
                        color="red"
                        align="right"
                        showBar={true}
                    />
                </td>
            )}
            {isVisible('volume') && (
                <td className={cn(tdBase, callBg)}>
                    <GhostCell
                        val={ceVol}
                        max={maxValues.maxVol}
                        fmt={`${(ceVol / 10000000).toFixed(2)}Cr`}
                        color="red"
                        align="right"
                        showBar={true}
                    />
                </td>
            )}
            {isVisible('oi') && (
                <td className={cn(tdBase, callBg)}>
                    <GhostCell
                        val={ceOI}
                        max={maxValues.maxOI}
                        fmt={`${(ceOI / 100000).toFixed(1)}L`}
                        color="red"
                        align="right"
                    />
                </td>
            )}
            {isVisible('change_p') && (
                <td className={cn(tdBase, callBg)}>
                    <GhostCell
                        val={ceChangeP}
                        max={100}
                        fmt={`${ceChangeP.toFixed(1)}%`}
                        color="red"
                        align="right"
                        showBar={true}
                    />
                </td>
            )}
            {isVisible('ltp') && (
                <td className={cn(tdBase, callBg, 'text-right')}>
                    <GhostCell
                        val={ceLtp}
                        max={maxValues.maxLtp}
                        fmt={ceLtp.toFixed(2)}
                        color="red"
                        align="right"
                        showBar={true}
                    />
                </td>
            )}

            <td
                className={`p-0 align-middle sticky left-0 z-10 text-center px-1 border-b border-slate-100/30 dark:border-slate-800/30 shadow-sm ${isCeAtm ? 'bg-transparent animate-breathe-atm border-y border-yellow-200/50 dark:border-yellow-600/30' : 'bg-white/70 dark:bg-slate-900/70 backdrop-blur-md'}`}
            >
                <StrikeChip strike={ceStrike} label={ceLabel?.text} />
            </td>

            {/* PE Side */}
            <td
                className={`p-0 align-middle sticky left-0 z-10 text-center px-1 border-b border-l border-slate-100/30 dark:border-slate-800/30 shadow-sm ${isPeAtm ? 'bg-transparent animate-breathe-atm border-y border-yellow-200/50 dark:border-yellow-600/30' : 'bg-white/70 dark:bg-slate-900/70 backdrop-blur-md'}`}
            >
                <StrikeChip strike={peStrike} label={peLabel?.text} />
            </td>

            {isVisible('ltp') && (
                <td className={cn(tdBase, putBg, 'text-left')}>
                    <GhostCell
                        val={peLtp}
                        max={maxValues.maxLtp}
                        fmt={peLtp.toFixed(2)}
                        color="green"
                        align="left"
                        showBar={true}
                    />
                </td>
            )}
            {isVisible('change_p') && (
                <td className={cn(tdBase, putBg)}>
                    <GhostCell
                        val={peChangeP}
                        max={100}
                        fmt={`${peChangeP.toFixed(1)}%`}
                        color="green"
                        align="left"
                        showBar={true}
                    />
                </td>
            )}
            {isVisible('oi') && (
                <td className={cn(tdBase, putBg)}>
                    <GhostCell
                        val={peOI}
                        max={maxValues.maxOI}
                        fmt={`${(peOI / 100000).toFixed(1)}L`}
                        color="green"
                        align="left"
                    />
                </td>
            )}
            {isVisible('volume') && (
                <td className={cn(tdBase, putBg)}>
                    <GhostCell
                        val={peVol}
                        max={maxValues.maxVol}
                        fmt={`${(peVol / 10000000).toFixed(2)}Cr`}
                        color="green"
                        align="left"
                        showBar={true}
                    />
                </td>
            )}
            {isVisible('iv') && (
                <td className={cn(tdBase, putBg, 'text-left last:rounded-r-[12px]')}>
                    <GhostCell
                        val={peIV || 0}
                        max={maxValues.maxIV}
                        fmt={typeof peIV === 'number' ? peIV.toFixed(1) : '-'}
                        color="green"
                        align="left"
                        showBar={true}
                    />
                </td>
            )}
        </tr>
    )
}, rowAreEqual);

const DashboardRankingTableContent: React.FC<DashboardRankingTableProps> = React.memo(
    ({ marketData, settings, analysis, className = '' }) => {
        // Top Section State (Ranking Table)
        const [rankRowsPerPage, setRankRowsPerPage] = React.useState(() => {
            if (typeof window !== 'undefined') {
                const s = localStorage.getItem(CONFIG.UI.STORAGE_KEYS.DASHBOARD_RANKING_ROWS)
                return s ? parseInt(s, 10) : 10
            }
            return 10
        })

        const [visibleLimit, setVisibleLimit] = React.useState(rankRowsPerPage)

        // Use centralized column visibility hook
        const defaultColumns: ColumnConfig[] = DASHBOARD_RANKING_COLUMNS
        const [columns, toggleColumn, isVisible] = useColumnVisibility(
            CONFIG.UI.STORAGE_KEYS.DASHBOARD_RANKING_COLUMNS,
            defaultColumns,
        )
        
        const isMounted = useFastMount()

        React.useEffect(() => {
            localStorage.setItem(CONFIG.UI.STORAGE_KEYS.DASHBOARD_RANKING_ROWS, rankRowsPerPage.toString())
        }, [rankRowsPerPage])

        React.useEffect(() => {
            setVisibleLimit(rankRowsPerPage)
        }, [rankRowsPerPage])

        const getRankedData = React.useCallback(
            (options: OptionData[], type: 'CE' | 'PE', mode: AnalysisMode) => {
                if (!options) return []
                const step = getStrikeStep(marketData?.symbol || '', options)
                const atm = Math.round((marketData?.spotPrice ?? 0) / (step || 1)) * (step || 1)
                const range = settings.range || 10
                const filtered = options.filter((opt) => {
                    const strike = getFieldValue(opt, FIELD_KEYS.STRIKE)
                    return Math.abs(strike - atm) <= range * step
                })

                return getSortedOptions(filtered, mode, type)
            },
            [marketData, settings],
        )

        const ceRanked = useMemo(
            () => (marketData ? getRankedData(marketData.options, 'CE', settings.mode) : []),
            [marketData, settings.mode, getRankedData],
        )
        const peRanked = useMemo(
            () => (marketData ? getRankedData(marketData.options, 'PE', settings.mode) : []),
            [marketData, settings.mode, getRankedData],
        )

        const mergedRows = useMemo(() => {
            const maxLen = Math.max(ceRanked.length, peRanked.length)
            const rows = []
            for (let i = 0; i < maxLen; i++) {
                rows.push({ ce: ceRanked[i], pe: peRanked[i] })
            }
            return rows
        }, [ceRanked, peRanked])

        const maxValues = useMemo(() => {
            let maxVol = 1,
                maxOI = 1,
                maxLtp = 1,
                maxIV = 1
            mergedRows.forEach((row) => {
                if (row.ce) {
                    maxVol = Math.max(maxVol, getFieldValue(row.ce, FIELD_KEYS.CE_VOL) || 0)
                    maxOI = Math.max(maxOI, getFieldValue(row.ce, FIELD_KEYS.CE_OI) || 0)
                    maxLtp = Math.max(maxLtp, getFieldValue(row.ce, FIELD_KEYS.CE_LTP) || 0)
                    maxIV = Math.max(maxIV, getFieldValue(row.ce, FIELD_KEYS.CE_IV) || 0)
                }
                if (row.pe) {
                    maxVol = Math.max(maxVol, getFieldValue(row.pe, FIELD_KEYS.PE_VOL) || 0)
                    maxOI = Math.max(maxOI, getFieldValue(row.pe, FIELD_KEYS.PE_OI) || 0)
                    maxLtp = Math.max(maxLtp, getFieldValue(row.pe, FIELD_KEYS.PE_LTP) || 0)
                    maxIV = Math.max(maxIV, getFieldValue(row.pe, FIELD_KEYS.PE_IV) || 0)
                }
            })
            return { maxVol, maxOI, maxLtp, maxIV }
        }, [mergedRows])

        const paginatedMergedRows = useMemo(() => {
            const sliced = mergedRows.slice(0, visibleLimit)
            return isMounted ? sliced : sliced.slice(0, 3)
        }, [mergedRows, visibleLimit, isMounted])

        // 🚀 PERF FIX: Removed useDeferredValue to prevent render starvation.
        // Because the data updates every 2 seconds, a deferred value gets continuously
        // interrupted and aborted by React, causing the UI to freeze and never show live data.
        const deferredRows = paginatedMergedRows

        return (
            <TableWrapper className={className}>
                <div className="px-6 py-5 flex flex-wrap gap-y-4 justify-between items-center border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-t-[20px] transition-colors duration-300">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl text-emerald-600 dark:text-emerald-400 shadow-sm ring-1 ring-emerald-100 dark:ring-emerald-800">
                            <Award size={22} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                {settings.mode === AnalysisMode.OI ? 'Open Interest Ranking' : 'Volume Ranking'}
                            </h2>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-medium">
                                Top strikes sorted by {settings.mode}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <select
                            value={rankRowsPerPage}
                            onChange={(e) => setRankRowsPerPage(Number(e.target.value))}
                            className="h-9 pl-3 pr-8 text-xs font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 cursor-pointer shadow-sm hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                        >
                            {[5, 10, 20, 50].map((n) => (
                                <option
                                    key={n}
                                    value={n}
                                    className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                                >
                                    {n} rows
                                </option>
                            ))}
                        </select>
                        <ColumnSelector columns={columns} onChange={toggleColumn} />
                    </div>
                </div>

                <div
                    className="overflow-x-auto w-full max-w-full"
                    style={{ '--header-height': '0px' } as React.CSSProperties}
                >
                    <table className="w-full border-separate border-spacing-y-[2px] mx-auto">
                        {/* Dynamic colgroup for consistent alignment */}
                        <colgroup>{/* CE data columns */}{isVisible('iv') && <col style={{ width: '70px' }} />}{isVisible('volume') && <col style={{ width: '90px' }} />}{isVisible('oi') && <col style={{ width: '80px' }} />}{isVisible('change_p') && <col style={{ width: '70px' }} />}{isVisible('ltp') && <col style={{ width: '80px' }} />}<col style={{ width: '90px' }} /><col style={{ width: '90px' }} />{/* PE data columns */}{isVisible('ltp') && <col style={{ width: '80px' }} />}{isVisible('change_p') && <col style={{ width: '70px' }} />}{isVisible('oi') && <col style={{ width: '80px' }} />}{isVisible('volume') && <col style={{ width: '90px' }} />}{isVisible('iv') && <col style={{ width: '70px' }} />}</colgroup>
                        <thead className="group">
                            <tr>
                                <th
                                    colSpan={100}
                                    className="p-0 border-none sticky top-0 z-40 bg-white dark:bg-slate-900 transition-colors duration-300"
                                >
                                    <div className="flex w-full shadow-sm">
                                        <div className="flex-1 bg-linear-to-b from-white to-rose-50 dark:from-slate-900 dark:to-rose-900/20 border-b-2 border-rose-100 dark:border-rose-800/50 p-2 text-center rounded-tl-[12px]">
                                            <div className="inline-flex px-4 py-1.5 rounded-full text-[10px] font-extrabold tracking-wider border border-rose-200/60 dark:border-rose-800/60 shadow-sm bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300">
                                                CALLS
                                            </div>
                                        </div>
                                        <div className="flex-1 bg-linear-to-b from-white to-emerald-50 dark:from-slate-900 dark:to-emerald-900/20 border-b-2 border-emerald-100 dark:border-emerald-800/50 p-2 text-center rounded-tr-[12px]">
                                            <div className="inline-flex px-4 py-1.5 rounded-full text-[10px] font-extrabold tracking-wider border border-emerald-200/60 dark:border-emerald-800/60 shadow-sm bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                                                PUTS
                                            </div>
                                        </div>
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                {/* Call Headers */}
                                {isVisible('iv') && (
                                    <GhostTh side="call" align="right">
                                        IV
                                    </GhostTh>
                                )}
                                {isVisible('volume') && (
                                    <GhostTh side="call" align="right">
                                        Vol
                                    </GhostTh>
                                )}
                                {isVisible('oi') && (
                                    <GhostTh side="call" align="right">
                                        OI
                                    </GhostTh>
                                )}
                                {isVisible('change_p') && (
                                    <GhostTh side="call" align="right">
                                        Chg%
                                    </GhostTh>
                                )}
                                {isVisible('ltp') && (
                                    <GhostTh side="call" align="right">
                                        LTP
                                    </GhostTh>
                                )}
                                {/* Call Strike */}
                                <th className="sticky top-[46px] z-30 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-[10px] font-mono font-bold tracking-widest text-slate-500 dark:text-slate-400 text-center min-w-[100px] p-2 transition-colors duration-300">
                                    STRIKE
                                </th>

                                {/* Put Strike */}
                                <th className="sticky top-[46px] z-30 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-[10px] font-mono font-bold tracking-widest text-slate-500 dark:text-slate-400 text-center min-w-[100px] p-2 border-l dark:border-l-slate-700 transition-colors duration-300">
                                    STRIKE
                                </th>
                                {isVisible('ltp') && (
                                    <GhostTh side="put" align="left">
                                        LTP
                                    </GhostTh>
                                )}
                                {isVisible('change_p') && (
                                    <GhostTh side="put" align="left">
                                        Chg%
                                    </GhostTh>
                                )}
                                {isVisible('oi') && (
                                    <GhostTh side="put" align="left">
                                        OI
                                    </GhostTh>
                                )}
                                {isVisible('volume') && (
                                    <GhostTh side="put" align="left">
                                        Vol
                                    </GhostTh>
                                )}
                                {isVisible('iv') && (
                                    <GhostTh side="put" align="left">
                                        IV
                                    </GhostTh>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/0">
                            {(() => {
                                // PERF FIX: Calculate step and indexLtp ONCE outside the loop.
                                // Previously getStrikeStep() was called per row, iterating the
                                // options array each time — O(n*m) per render tick.
                                const step = marketData ? getStrikeStep(marketData.symbol, marketData.options) : 50
                                const indexLtp = marketData?.spotPrice || 0
                                const halfStep = step / 2

                                return deferredRows.map((row, idx) => {
                                    const ceStrike = getFieldValue(row.ce, FIELD_KEYS.STRIKE) || 0
                                    const isCeAtm = Math.abs(ceStrike - indexLtp) < halfStep
                                    const peStrike = getFieldValue(row.pe, FIELD_KEYS.STRIKE) || 0
                                    const isPeAtm = Math.abs(peStrike - indexLtp) < halfStep
                                    
                                    return (
                                        <DashboardRankingRow 
                                            key={`${ceStrike}-${peStrike}`}
                                            row={row}
                                            idx={idx}
                                            analysis={analysis}
                                            isVisible={isVisible}
                                            maxValues={maxValues}
                                            settingsMode={settings.mode}
                                            isCeAtm={isCeAtm}
                                            isPeAtm={isPeAtm}
                                        />
                                    )
                                })
                            })()}
                        </tbody>
                    </table>
                </div>

                {/* ── Footer: Load More ── */}
                { mergedRows.length > visibleLimit && (
                    <div className="flex items-center justify-center p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
                        <button
                            onClick={ () => setVisibleLimit( prev => prev + rankRowsPerPage ) }
                            className="h-10 px-6 text-xs font-bold rounded-xl border border-primary/20 bg-primary/10 text-primary hover:bg-primary/20 transition-colors inline-flex items-center gap-2 shadow-sm"
                        >
                            Load Next { rankRowsPerPage } Rows
                            <span className="text-[10px] font-medium text-primary/70">
                                ({ mergedRows.length - visibleLimit } remaining)
                            </span>
                            <ChevronRight className="h-4 w-4 opacity-70" />
                        </button>
                    </div>
                ) }
            </TableWrapper>
        )
    },
)
